export type SystemSettings = {
  platformName: string;
  aiModel: string;
  checkInterval: string;
  wordpressTakeover: boolean;
  autoHttps: boolean;
  quotaAlert: string;
  logoDataUrl: string;
  faviconDataUrl: string;
  platformSeoTitle: string;
  platformSeoKeywords: string;
  platformSeoDescription: string;
  defaultArticleModel: string;
  defaultImageModel: string;
  modelRoutingVersion: string;
  keywordCleanModel: string;
  keywordTitleModel: string;
  siteBuilderModel: string;
  siteTdkModel: string;
  contentTitleModel: string;
  seoDiagnosisModel: string;
  geoDiagnosisModel: string;
  reportModel: string;
  assistantModel: string;
  modelFallbackOrder: string;
  allowEmployeeModelSwitch: boolean;
  defaultMemberRole: string;
  availableTeamRoles: string;
  defaultTaskReviewer: string;
  requireTaskReview: boolean;
  taskOverdueAlertDays: string;
  dailyReportTime: string;
  customerReportCycle: string;
  seoDefaultDepth: string;
  seoMaxPages: string;
  seoPageTimeout: string;
  seoSearchEngines: string;
  seoScoreWeights: string;
  geoModelScope: string;
  geoPromptSampleSize: string;
  geoScoreWeights: string;
  contentDefaultWords: string;
  contentDefaultImages: string;
  contentPublishStatus: string;
  contentComplianceMode: string;
  autoGenerateCover: boolean;
  autoInternalLinks: boolean;
  notifySiteDown: boolean;
  notifyTaskOverdue: boolean;
  notifyPublishFailed: boolean;
  notifyReportReady: boolean;
  loginFailureLimit: string;
  passwordMinLength: string;
  auditLogRetentionDays: string;
  requireDangerConfirm: boolean;
};

export const SYSTEM_SETTINGS_KEY = "seo-nexus-system-settings";
export const SYSTEM_SETTINGS_UPDATED = "seo-nexus-system-settings-updated";
export const DEFAULT_FAVICON_URL = "/favicon.svg";
export const DEFAULT_APPLE_TOUCH_ICON_URL = "/apple-touch-icon.png";

const defaultLogoSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="360" height="96" viewBox="0 0 360 96" fill="none">
  <defs><linearGradient id="brand" x1="12" y1="12" x2="84" y2="84" gradientUnits="userSpaceOnUse"><stop stop-color="#0AA878"/><stop offset=".5" stop-color="#078866"/><stop offset="1" stop-color="#076A51"/></linearGradient></defs>
  <rect x="12" y="12" width="72" height="72" rx="16" fill="url(#brand)"/>
  <svg x="12" y="12" width="72" height="72" viewBox="0 0 64 64">
    <path d="M19 14v34h29" fill="none" stroke="#fff" stroke-width="10" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M39.5 30.5C48 21.5 56 22 56 22c-.5 9.5-7.5 17-17.5 17.5 1.2-4.5 1.4-6.8 1-9Z" fill="#F7FFFC"/>
    <path d="M43.8 35.4c2.4-3.7 5.2-6.2 8.4-8.2" fill="none" stroke="#9DE6CD" stroke-width="2.6" stroke-linecap="round"/>
  </svg>
  <text x="104" y="45" font-family="Inter,Arial,sans-serif" font-size="28" font-weight="900" fill="#14241F">良木AI</text>
  <text x="104" y="68" font-family="Inter,Arial,sans-serif" font-size="13" font-weight="600" fill="#5F706B">AI 全域营销增长平台</text>
</svg>`;

const svgToDataUrl = (svg: string) =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg.replace(/\s+/g, " ").trim())}`;

export const defaultSystemSettings: SystemSettings = {
  platformName: "良木AI",
  aiModel: "GPT-5 SEO Agent",
  checkInterval: "30",
  wordpressTakeover: true,
  autoHttps: true,
  quotaAlert: "80",
  logoDataUrl: svgToDataUrl(defaultLogoSvg),
  faviconDataUrl: DEFAULT_FAVICON_URL,
  platformSeoTitle: "良木AI - AI 全域营销增长平台",
  platformSeoKeywords: "良木AI,AI SEO,GEO优化,AI建站,关键词拓词,AI内容工厂,WordPress站群",
  platformSeoDescription: "良木AI 是一套面向站群管理、AI建站、关键词拓展、内容生产和WordPress站点SEO优化的全域营销增长平台。",
  defaultArticleModel: "DeepSeek Chat",
  defaultImageModel: "SiliconFlow Z-Image",
  modelRoutingVersion: "2",
  keywordCleanModel: "自动选择",
  keywordTitleModel: "豆包 / 火山方舟",
  siteBuilderModel: "DeepSeek Chat",
  siteTdkModel: "豆包 / 火山方舟",
  contentTitleModel: "豆包 / 火山方舟",
  seoDiagnosisModel: "OpenAI",
  geoDiagnosisModel: "豆包 / 火山方舟",
  reportModel: "DeepSeek Chat",
  assistantModel: "DeepSeek Chat",
  modelFallbackOrder: "DeepSeek Chat > 豆包 / 火山方舟 > 通义千问",
  allowEmployeeModelSwitch: false,
  defaultMemberRole: "内容运营",
  availableTeamRoles: "主管,项目经理,SEO顾问,内容运营,建站交付,客服",
  defaultTaskReviewer: "主管",
  requireTaskReview: true,
  taskOverdueAlertDays: "1",
  dailyReportTime: "18:30",
  customerReportCycle: "月报",
  seoDefaultDepth: "标准诊断",
  seoMaxPages: "80",
  seoPageTimeout: "12",
  seoSearchEngines: "百度,360搜索,搜狗,Bing,Google",
  seoScoreWeights: "抓取收录30,站内结构35,外链权威15,速度体验20",
  geoModelScope: "ChatGPT,豆包,文心一言,DeepSeek,Kimi,通义千问,腾讯元宝,Gemini",
  geoPromptSampleSize: "20",
  geoScoreWeights: "AI提及率30,引用来源25,答案排名20,内容缺口15,竞品差距10",
  contentDefaultWords: "1200-1800",
  contentDefaultImages: "3",
  contentPublishStatus: "待审核",
  contentComplianceMode: "开启",
  autoGenerateCover: true,
  autoInternalLinks: true,
  notifySiteDown: true,
  notifyTaskOverdue: true,
  notifyPublishFailed: true,
  notifyReportReady: true,
  loginFailureLimit: "5",
  passwordMinLength: "8",
  auditLogRetentionDays: "180",
  requireDangerConfirm: true,
};

function migrateLegacySettings(settings: SystemSettings, legacyModelRouting = false): SystemSettings {
  const imageModel = /gemini|imagen/i.test(settings.defaultImageModel)
    ? "SiliconFlow Z-Image"
    : legacyModelRouting && settings.defaultImageModel === "OpenAI GPT Image"
      ? "SiliconFlow Z-Image"
      : settings.defaultImageModel;
  return {
    ...settings,
    platformName: settings.platformName === "SEOAgent 流量工厂" ? defaultSystemSettings.platformName : settings.platformName,
    logoDataUrl: !settings.logoDataUrl || String(settings.logoDataUrl || "").includes("SEOAgent") ? defaultSystemSettings.logoDataUrl : settings.logoDataUrl,
    faviconDataUrl: settings.faviconDataUrl || DEFAULT_FAVICON_URL,
    platformSeoTitle: String(settings.platformSeoTitle || "").includes("SEOAgent") ? defaultSystemSettings.platformSeoTitle : settings.platformSeoTitle,
    platformSeoKeywords: String(settings.platformSeoKeywords || "").includes("SEOAgent") ? defaultSystemSettings.platformSeoKeywords : settings.platformSeoKeywords,
    platformSeoDescription: String(settings.platformSeoDescription || "").includes("SEOAgent") ? defaultSystemSettings.platformSeoDescription : settings.platformSeoDescription,
    defaultImageModel: imageModel,
    siteBuilderModel: defaultSystemSettings.siteBuilderModel,
    modelRoutingVersion: defaultSystemSettings.modelRoutingVersion,
  };
}

export function loadSystemSettings(): SystemSettings {
  if (typeof window === "undefined") return defaultSystemSettings;
  try {
    const cached = window.localStorage.getItem(SYSTEM_SETTINGS_KEY);
    if (!cached) return defaultSystemSettings;
    const source = JSON.parse(cached) as Partial<SystemSettings>;
    const legacyModelRouting = !Object.prototype.hasOwnProperty.call(source, "modelRoutingVersion");
    return migrateLegacySettings({ ...defaultSystemSettings, ...source }, legacyModelRouting);
  } catch {
    return defaultSystemSettings;
  }
}

export function applySystemBrand(settings: SystemSettings) {
  if (typeof document === "undefined") return;
  document.title = settings.platformSeoTitle || settings.platformName || defaultSystemSettings.platformName;

  const upsertMeta = (name: string, content: string) => {
    let meta = document.querySelector<HTMLMetaElement>(`meta[name='${name}']`);
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = name;
      document.head.appendChild(meta);
    }
    meta.content = content;
  };

  upsertMeta("keywords", settings.platformSeoKeywords || defaultSystemSettings.platformSeoKeywords);
  upsertMeta("description", settings.platformSeoDescription || defaultSystemSettings.platformSeoDescription);

  document.querySelectorAll<HTMLLinkElement>("link[rel='icon'], link[rel='shortcut icon']").forEach((item, index) => {
    if (index > 0) item.remove();
  });

  let icon = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (!icon) {
    icon = document.createElement("link");
    icon.rel = "icon";
    document.head.appendChild(icon);
  }
  icon.type = "image/svg+xml";
  icon.href = settings.faviconDataUrl || DEFAULT_FAVICON_URL;

  let alternateIcon = document.querySelector<HTMLLinkElement>("link[rel='alternate icon']");
  if (!alternateIcon) {
    alternateIcon = document.createElement("link");
    alternateIcon.rel = "alternate icon";
    document.head.appendChild(alternateIcon);
  }
  alternateIcon.href = "/favicon.ico";

  let appleIcon = document.querySelector<HTMLLinkElement>("link[rel='apple-touch-icon']");
  if (!appleIcon) {
    appleIcon = document.createElement("link");
    appleIcon.rel = "apple-touch-icon";
    document.head.appendChild(appleIcon);
  }
  appleIcon.href = DEFAULT_APPLE_TOUCH_ICON_URL;
}

export function saveSystemSettings(settings: SystemSettings) {
  const normalized = migrateLegacySettings({ ...defaultSystemSettings, ...settings });
  window.localStorage.setItem(SYSTEM_SETTINGS_KEY, JSON.stringify(normalized));
  applySystemBrand(normalized);
  window.dispatchEvent(new CustomEvent(SYSTEM_SETTINGS_UPDATED, { detail: normalized }));
}
